import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import apiClient from "@/repositories/apiClient";
import { showToast } from "@/redux/new_services/toasts";
import { useDispatch } from "react-redux";
import { useHasChanged } from "@/components/Helpers";
import { UserContext } from "./UserContextProvider";

class RetriableError extends Error {}
class FatalError extends Error {}

const host = `${process.env.SERVER_URL}`;

export const StreamServiceContext = createContext();

export const StreamServiceProvider = ({ children }) => {
  const { token } = useContext(UserContext);
  const [isConnected, setIsConnected] = useState(false);
  const hasIsConnectedChanged = useHasChanged(isConnected);
  const subscribers = useRef({});
  const [sessionID, setSessionID] = useState(null);
  const controllerRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    // Connection state changes are noisy at runtime — every reload, every
    // strict-mode remount and every transient network blip would pop a
    // toast. We log them for debugging but no longer surface them to the
    // operator since the actual API errors raise their own toasts where
    // it matters.
    if (hasIsConnectedChanged) {
      if (isConnected) {
        console.info("connected to server");
      } else if (sessionID != null) {
        console.info("disconnected from server");
      }
    }
  }, [sessionID, hasIsConnectedChanged, isConnected, dispatch]);

  useEffect(() => {
    // Without a token there is nothing to authenticate; skip entirely
    // so we don't hammer the server with `Authorization: Token null`
    // during the logout→login gap (which otherwise leaves stale state
    // on the backend).
    if (!token) {
      // Make sure consumers wake up with no sessionID — the resync
      // effect below only fires once a fresh sessionID arrives, which
      // is exactly what we want on the next sign-in.
      setSessionID(null);
      setIsConnected(false);
      return undefined;
    }

    // Always create a fresh controller per effect run.
    // React 19 strict mode invokes the effect twice (mount → cleanup → mount);
    // reusing an aborted controller would cause the second fetchEventSource to
    // fail immediately with the previous abort signal.
    const controller = new AbortController();
    controllerRef.current = controller;

    console.info("triggerConnect to Server");

    const fetchData = async () => {
      await fetchEventSource(`${host}/sse/connect`, {
        method: "GET",
        headers: {
          Authorization: `Token ${token}`,
        },
        signal: controller.signal,
        async onopen(response) {
          if (response.ok && response.headers.get("content-type") === "text/event-stream") {
            setIsConnected(true);
            return; // everything's good
          } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            // client-side errors are usually non-retriable:
            throw new FatalError();
            //throw new RetriableError();
          } else {
            throw new RetriableError();
          }
        },
        onmessage(raw_msg) {
          // if the server emits an error message, throw an exception
          // so it gets handled by the onerror callback below:
          if (raw_msg.event === "FatalError") {
            throw new FatalError(raw_msg.data);
          }

          try {
            let o = JSON.parse(raw_msg.data);

            if (o.session_id) {
              setSessionID(o.session_id);
              return;
            }

            Object.entries(subscribers.current).forEach(([topic, { callbacks }]) => {
              if (o.primaryTopic === topic) {
                callbacks.forEach((callback) => {
                  callback(o);
                });
              }
            });

            // daten weiterleiten
          } catch (e) {
            console.info("error while parsing data", e);
          }
        },
        onclose() {
          setIsConnected(false);
          // if the server closes the connection unexpectedly, retry:
          throw new RetriableError();
        },
        onerror(err) {
          setIsConnected(false);
          if (err instanceof FatalError) {
            console.info("FatalError", err);
            //throw err; // rethrow to stop the operation
          } else {
            // do nothing to automatically retry. You can also
            // return a specific retry interval here.
          }
        },
      });
    };

    fetchData();

    return () => {
      controller.abort();
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
      setIsConnected(false);
      // Drop the now-defunct sessionID. The [sessionID] resync effect
      // below is gated on `sessionID != null`, so this prevents it
      // from racing against the still-pending fresh sessionID from a
      // new token by firing first with the previous one.
      setSessionID(null);
    };
  }, [token]); // auch wenn es eine warnung gibt, ich lasse bewusst die subscriptions abhängigkeit weg...

  // Every time we receive a fresh session_id (initial connect or reconnect
  // after the SSE stream dropped), the backend has zero knowledge of which
  // topics this dashboard was previously interested in — its in-memory
  // subscription map is keyed by session id. So we replay every known
  // subscription against the new session, unconditionally. Backend
  // subscribeToTopic is idempotent (controllers/serverstream/mqtt.go
  // already guards with `slices.Contains`), so re-registering an existing
  // topic is harmless and re-replays the cached retained message back to
  // the freshly opened SSE channel.
  useEffect(() => {
    if (sessionID == null) return;
    const topics = Object.keys(subscribers.current);
    if (topics.length === 0) return;
    console.info("re-syncing topic subscriptions after sessionID change", topics);
    topics.forEach((topic) => {
      subscribeStreamService(topic, sessionID);
    });
  }, [sessionID]);

  // Keep latest sessionID accessible inside stable callbacks
  const sessionIDRef = useRef(sessionID);
  useEffect(() => {
    sessionIDRef.current = sessionID;
  }, [sessionID]);

  // Subscribe to a topic. The subscription is bookkept locally so the
  // sessionID effect above can replay all topics on reconnect; the actual
  // POST to the backend only fires if we already know a sessionID,
  // otherwise the effect picks it up as soon as we have one.
  const subscribe = useCallback((topic, callback) => {
    if (!subscribers.current[topic]) {
      subscribers.current[topic] = { count: 1, callbacks: [callback] };
    } else {
      subscribers.current[topic].count += 1;
      subscribers.current[topic].callbacks.push(callback);
    }
    const currentSessionID = sessionIDRef.current;
    if (currentSessionID != null) {
      // TODO: Das senden des subscribe an den Server eventuell verzögern, so das dies gebündelt versendet werden kann
      subscribeStreamService(topic, currentSessionID);
    }
  }, []);

  // Funktion zum Kündigen des Abonnements
  // TODO: Das senden des unsubscribe an den Server eventuell verzögern, so das dies gebündelt versendet werden kann
  const unsubscribe = useCallback(async (topic) => {
    // Verringere den Zähler und entferne den Subscriber, wenn es der letzte ist
    if (subscribers.current[topic] && subscribers.current[topic].count > 1) {
      subscribers.current[topic].count -= 1;
      // Optional: Entferne die spezifische Callback-Funktion aus der Liste
    } else {
      delete subscribers.current[topic];
      const currentSessionID = sessionIDRef.current;
      if (currentSessionID != null) {
        unsubscribeStreamService(topic, currentSessionID);
      }
    }
  }, []);

  const value = useMemo(
    () => ({ isConnected, subscribe, unsubscribe, sessionID }),
    [isConnected, subscribe, unsubscribe, sessionID],
  );

  return <StreamServiceContext.Provider value={value}>{children}</StreamServiceContext.Provider>;
};

// means, it checks for free licences
const subscribeStreamService = async (topic, sessionID) => {
  try {
    await apiClient.post(`/sse/${sessionID}/subscribe`, JSON.stringify({ topics: [topic] }));
  } catch {
    return false;
  }
  return true;
};

const unsubscribeStreamService = async (topic, sessionID) => {
  try {
    console.info("unsubscribe");
    await apiClient.post(`/sse/${sessionID}/unsubscribe`, JSON.stringify({ topics: [topic] }));
  } catch {
    // fehler
    return false;
  }

  return true;
};

export const useStreamService = () => useContext(StreamServiceContext);
